<template>
  <el-tooltip placement="top" :open-delay="500">
    <div slot="content">
      {{event.client || "- - -"}}<br/>
      {{event.project}}<br/>
      {{event.task}}<br/>
      <span v-if="event.description">{{event.description}}<br/></span>
      {{event.startTime.format('HH:mm')}} to {{event.endTime.format('HH:mm')}}<br/>
      {{event.totalTime}}<br/>
    </div>
    <div ref="event_block" class="v-cal-event-item" :class="eventClasses" @click.stop="eventClicked" :style="eventStyles">
      <div class="v-cal-event-name client" v-if="displayHeight >= 26">{{ event.client }}</div>
      <div class="v-cal-event-name project" v-if="displayHeight >= 56">{{ event.project }}</div>
      <div class="v-cal-event-name task" v-if="displayHeight >= 70">{{ event.task }}</div>
      <div class="v-cal-event-name description" v-if="displayHeight >= 84">{{ event.description }}</div>
      <div class="v-cal-event-name time" v-if="displayHeight >= 98">{{event.startTime.format('HH:mm')}} to {{event.endTime.format('HH:mm')}}</div>
      <div class="v-cal-event-name total" v-if="displayHeight >= 42">{{ event.totalTime }}</div>
      <div class="v-cal-event-name split-indicator" v-if="event.isSplitStart"><font-awesome-icon icon="caret-down" class="menu-icon" /></div>
      <div class="v-cal-event-name split-indicator" v-if="event.isSplitEnd"><font-awesome-icon icon="caret-up" class="menu-icon" /></div>
    </div>
  </el-tooltip>
</template>

<script>
import { EventBus } from './EventBus'
import moment from 'moment'

export default {
  name: 'EventItem',
  props: {
    event: {
      type: Object,
      required: true
    },
    use12: {
      type: Boolean,
      required: true
    },
    hasDynamicSize: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      ancestorHeight: 0
    }
  },
  mounted () {
    if (this.hasDynamicSize) {
      this.getAndSetAncestorHeight()
      window.addEventListener('resize', this.getAndSetAncestorHeight)

      let hourBlock = this.findAncestor(this.$el, 'v-cal-day__hour-block')
      let observer = new MutationObserver(mutations => {
        mutations.forEach(mutation => mutation.type === 'attributes' && !this._isDestroyed && this.$nextTick(() => this.getAndSetAncestorHeight()))
      })
      observer.observe(hourBlock, { attributes: true })
    }
  },
  beforeDestroy () {
    if (this.hasDynamicSize) { window.removeEventListener('resize', this.getAndSetAncestorHeight) }
  },
  methods: {
    eventClicked (event) {
      if (!this.event.isPTO && !this.event.isHoliday) {
        EventBus.$emit('event-clicked', this.event)
      }
    },
    getAndSetAncestorHeight () {
      let hourBlock = this.findAncestor(this.$el, 'v-cal-day__hour-block')
      let padding = parseFloat(hourBlock.style.paddingTop || window.getComputedStyle(hourBlock, null).paddingTop)
      let height = parseFloat(window.getComputedStyle(hourBlock.querySelector('.v-cal-day__hour-block-fill', null)).height)
      this.ancestorHeight = padding + padding + height
    },
    findAncestor (el, cls) {
      while ((el = el.parentElement) && !el.classList.contains(cls)) ;
      return el
    }
  },
  computed: {
    displayHeight () {
      const end = this.event.startTime.isSame(this.event.endTime, 'day') ? this.event.endTime : moment(this.event.startTime).endOf('day')
      const hours = end.diff(this.event.startTime, 'hours', true)
      const bordersOffset = hours - 1
      return (hours * this.ancestorHeight) + bordersOffset
    },
    eventStyles () {
      let styles = []
      styles.push({
        'backgroundColor': this.event.color,
        'color': this.event.color,
        'margin-bottom': '0px !important',
        'position': 'absolute'
      })

      if (this.hasDynamicSize) {
        styles.push({
          'height': this.displayHeight + 'px'
        })

        if (this.event.overlaps > 0) {
          const width = 100 / (this.event.overlaps + 1)
          styles.push({
            'width': width + '%',
            'left': width + '%'
          })
        }

        if (this.event.startTime.minutes() > 0) {
          const distance = (this.ancestorHeight / 60) * this.event.startTime.minutes()
          styles.push({
            'top': distance + 'px'
          })
        }
      }

      return styles
    },
    eventClasses () {
      return [
        this.event.class,
        this.event.overlaps > 0 ? 'is-overlapping' : '',
        this.event.isSplitStart ? 'split-start' : '',
        this.event.isSplitEnd ? 'split-end' : ''
      ]
    }
  },
  filters: {
    formatEventTime (hour, use12 = false) {
      if (!hour) { return '' }

      if (use12) { return hour.format(hour.minutes() > 0 ? 'h.mma' : 'ha').slice(0, -1) }

      return hour.format(hour.minutes() > 0 ? 'HH.mm' : 'HH')
    }
  }
}
</script>

<style lang='scss'>
.v-cal-event-item{
  overflow: visible;
  padding: 0px 8px !important;
  box-shadow: none !important;
  border-radius: 5px;
  right: 5px;
  left: 0px;
  width: unset !important; 
  border-top: 1px solid #FFF;
  transition: all 0.3s ease-in-out;
  
  .split-indicator {
    font-size: 24px !important;
    position: absolute;
  }
  &.split-start {
    border-radius: 5px 5px 0px 0px;

    .split-indicator {
      bottom: -16px;
      left: calc(50% - 7px);
    }
  } 
  &.split-end {
    border-radius: 0px 0px 5px 5px;

    .split-indicator {
      top: -16px;
      left: calc(50% - 7px);
    }
  } 
  .v-cal-event-name {
    padding: 0px !important;
    margin: 0px !important;
    font-size: 12px;
    overflow: hidden;
    text-overflow: ellipsis;

    &:first-child {
      font-size: 14px;
      margin-top: 5px !important;
    }
  }
  .total {
    position: absolute;
    bottom: 5px;
    right: 10px;
    font-size: 14px;
  }
}
</style>

